import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import Sortable from "sortablejs"
import flash from "../lib/flashes.js"

export default class extends Controller {
  static targets = [
    "emptyState",
    "visualizer",
    "selectedFieldType",
    "sidebar",
    "fieldPropertiesSidebar",
    "fieldPropertiesFrame",
    "visualizerLabel",
    "fieldPropertiesLabel",
    "integratorSlugSelector",
    "integratorSlugGroup",
    "integratorDataPointGroup",
    "integratorDataPointSelector",
    "radarDataPointGroup",
    "radarDataPointSelector",
    "radarLocationGroup"
  ]

  static values = {
    selectedFieldId: String,
    scrollToFieldId: String,
    pendingFieldType: String,
    fieldPropertiesUrl: String,
    autosaveDebounceMs: { type: Number, default: 600 },
  }

  connect() {
    this.selectedType = null
    this.pendingPosition = null
    this.autosaveTimeout = null
    this.pendingAutosaveForm = null
    this.autosaveAbortControllersByForm = new Map()
    this.pendingAutosaveByForm = new Map()
    this.lastAutosavePayloadByForm = new Map()
    // Tracks edits to a visualizer label input that arrived while the field
    // properties frame was still loading; flushed in `handleFrameLoad`.
    this._formLoading = false
    this._pendingLabelAutosave = false

    if (this.hasVisualizerTarget) {
      this._initializeSortable()
    }

    const pendingType = this.pendingFieldTypeValue
    if (pendingType) {
      this._restorePendingNewFieldAfterError(pendingType)
    }

    const selectedVis = this.visualizerTarget.querySelector(
      `.form-builder-visualizer-field[data-id="${this.selectedFieldIdValue}"]`
    )
    if (selectedVis) {
      selectedVis.classList.add("selected")
      selectedVis.scrollIntoView({
        behavior: "instant",
        block: "center"
      })
      this._activateVisualizerLabelFor(selectedVis)
    } else {
      this._deactivateAllVisualizerLabels()
      // A duplicated Divider is never selected, so anchor the visualizer on it
      // instead of landing at the top after the redirect.
      this.visualizerTarget
        .querySelector(`.form-builder-visualizer-field[data-id="${this.scrollToFieldIdValue}"]`)
        ?.scrollIntoView({ behavior: "instant", block: "center" })
    }

    this.handleFrameLoad()
  }

  disconnect() {
    this._clearQueuedAutosave()

    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }

    if (this.sidebarSortable) {
      this.sidebarSortable.destroy()
      this.sidebarSortable = null
    }

    this.pendingPosition = null
  }

  handleFrameLoad(event) {
    if (event && event.target !== this.fieldPropertiesFrameTarget) return

    const form = this._currentForm()
    if (form) {
      // Stable id for form lookup; Rails generates a per-record id we don't
      // rely on elsewhere.
      form.id = "asset-layout-field-form"
      this._syncPendingPositionIntoForm(form)
    }

    // The visualizer's label input is the new "first focusable" input.
    // Falling back to the frame's first input keeps the previous behavior
    // (focus the hint or other field) when no active visualizer label exists.
    const activeLabel = this._activeVisualizerLabel()
    const focusTarget = activeLabel || (form && form.querySelector("input:not([type='hidden']), select, textarea"))
    if (focusTarget) {
      // If the user is mid-typing (race: edited the label before the frame
      // finished loading) avoid moving the caret. `focus()` is a no-op when
      // the element is already focused, so call it unconditionally.
      const wasFocused = document.activeElement === focusTarget
      focusTarget.focus()
      if (!wasFocused && typeof focusTarget.setSelectionRange === "function" && focusTarget.value) {
        const len = focusTarget.value.length
        focusTarget.setSelectionRange(len, len)
      }
    }

    if (form) {
      const payloadBeforeVisualizerSync = this._pendingLabelAutosave && this._pendingVisualizerLabelDiffersFromForm(form)
        ? this._serializeForm(form)
        : null

      this._syncFieldPropertiesLabelFromActiveVisualizer(form)
      this.lastAutosavePayloadByForm.set(this._formKey(form), payloadBeforeVisualizerSync || this._serializeForm(form))
    }

    this._formLoading = false

    if (this._pendingLabelAutosave) {
      this._pendingLabelAutosave = false
      if (form && this._hasValidIntegratorPair(form)) {
        this._scheduleAutosave(form, this.autosaveDebounceMsValue)
      }
    }

    window.initializeTippy?.(this.fieldPropertiesFrameTarget)

    this.fetchIntegratorDataPoints()
  }

  handleFormSubmit() {
    this._clearQueuedAutosave()
  }

  trackSidebarCloseStart(event) {
    this._openSidebarForVisualizerLabelPointerDown(event.target)
    this.sidebarCloseStartedInside = this._keepsPropertiesSidebarOpen(event.target)
  }

  maybeCloseSidebar(event) {
    const target = event.target

    setTimeout(() => {
      const shouldKeepOpen = this.sidebarCloseStartedInside ||
        this._keepsPropertiesSidebarOpen(target)
      this.sidebarCloseStartedInside = false

      if (!shouldKeepOpen) {
        this.closeSidebar()
      }
    }, 0)
  }

  _keepsPropertiesSidebarOpen(target) {
    if (!(target instanceof Element)) return false

    if (this.fieldPropertiesSidebarTarget?.contains(target)) return true
    if (target.closest(".manage_modal__wrapper")) return true
    if (target.closest(".selection-card")) return true
    if (this._isTiptapPortalElement(target)) return true

    const visualizerField = target.closest(".form-builder-visualizer-field")
    if (!visualizerField) return false

    if (this._hasPendingAutosave()) return true

    return visualizerField.classList.contains("selected") ||
      visualizerField === this.selectedFieldTypeTarget
  }

  _isTiptapPortalElement(target) {
    return Boolean(target.closest([
      ".tiptap-dropdown-menu-content",
      ".tiptap-menu-content",
      ".tiptap-suggestion-menu",
      "[data-selector^='tiptap-']",
      "[data-slot^='tiptap-dropdown-menu']",
    ].join(",")))
  }

  _openSidebarForVisualizerLabelPointerDown(target) {
    if (!this._isVisualizerLabel(target)) return

    const visualizerField = target.closest(".form-builder-visualizer-field")
    if (!visualizerField || visualizerField === this.selectedFieldTypeTarget) return
    if (!visualizerField.dataset.id) return

    // Match selectVisualizer: Heading/InstructionBlock stay inline-only (close
    // any open properties panel), and Divider has nothing to show.
    if (this._isPropertiesPanelSuppressedType(visualizerField.dataset.type)) return

    if (this._isInlineOnlyType(visualizerField.dataset.type)) {
      // Already selected, but a previous selection may have left the panel open —
      // clear it instead of loading Heading/InstructionBlock properties.
      if (visualizerField.classList.contains("selected")) {
        this._clearFieldPropertiesPanel()
        return
      }
      this._selectVisualizerField(visualizerField, { loadProperties: false })
      return
    }

    if (visualizerField.classList.contains("selected")) return

    this._selectVisualizerField(visualizerField)
  }

  _initializeSortable() {
    if (this.hasSidebarTarget) {
      this.sidebarSortable = new Sortable(this.sidebarTarget, {
        group: { name: "form-builder", pull: "clone", put: false },
        sort: false,
        filter: ".selection-card.disabled, .form-builder-sidebar-header",
        draggable: ".selection-card",
      ghostClass: "form-builder-sortable-placeholder",
      chosenClass: "form-builder-sortable-chosen",
      dragClass: "form-builder-sortable-drag",
      fallbackClass: "form-builder-sortable-drag",
      fallbackOnBody: true,
      forceFallback: true,
        onStart: this._handleSidebarDragStart.bind(this)
      })
    }

    this.sortable = new Sortable(this.visualizerTarget, {
      group: { name: "form-builder", pull: false, put: ["form-builder"] },
      handle: ".drag-handle",
      draggable: ".form-builder-visualizer-field",
      ghostClass: "form-builder-sortable-placeholder",
      chosenClass: "form-builder-sortable-chosen",
      dragClass: "form-builder-sortable-drag",
      fallbackClass: "form-builder-sortable-drag",
      fallbackOnBody: true,
      forceFallback: true,
      animation: 150,
      onStart: this._handleVisualizerDragStart.bind(this),
      onAdd: this._handleSidebarDrop.bind(this),
      onEnd: this._handleVisualizerDragEnd.bind(this)
    })
  }

  _handleSidebarDragStart() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.replaceWith(this.visualizerTarget)
      this.visualizerTarget.classList.remove("hidden")
    }

    this._setVisualizerDragging(true)
  }

  _handleVisualizerDragStart() {
    this._setVisualizerDragging(true)
  }

  _handleVisualizerDragEnd(event) {
    this._setVisualizerDragging(false)

    if (!event.item?.dataset?.id) return

    return this._updateOrder()
  }

  _setVisualizerDragging(isDragging) {
    if (!this.hasVisualizerTarget) return

    this.visualizerTarget.classList.toggle("form-builder-visualizer--dragging", isDragging)
  }

  _handleSidebarDrop(event) {
    this._revertEmptyLabelInSelectedField()
    this._flushPendingAutosave()

    const clone = event.item
    const dropIndex = event.newIndex
    const type = clone?.dataset?.type
    if (!type) {
      clone?.remove()
      this._setVisualizerDragging(false)
      return
    }

    clone.remove()

    const preview = this.selectedFieldTypeTarget
    const referenceNode = this.visualizerTarget.children[dropIndex] || null
    this.visualizerTarget.insertBefore(preview, referenceNode)

    let persistedBefore = 0
    for (const child of this.visualizerTarget.children) {
      if (child === preview) break
      if (child.classList.contains("form-builder-visualizer-field") && child.dataset.id) {
        persistedBefore += 1
      }
    }
    this.pendingPosition = persistedBefore + 1

    this.selectedType = type
    this._clearFormBuilderErrorHighlights()
    this._hideAllVisualizerForms()
    this._clearSelectedVisualizerForms()
    this._clearNewFieldPreviewLabel()
    this.selectedFieldTypeTarget.classList.remove("hidden")
    this._showFormForType(type)

    // Visual-only elements (Divider) have no editable properties; create them
    // directly without opening the field properties side panel.
    if (this._isPropertiesPanelSuppressedType(type)) {
      return this._createPropertiesPanelSuppressedField(type, this.pendingPosition)
    }

    // Heading/InstructionBlock are edited inline in the workspace — create immediately
    // and keep the side panel closed.
    if (this._isInlineOnlyType(type)) {
      return this._createInlineOnlyField(type, this.pendingPosition)
    }

    this._activateVisualizerLabelFor(this.selectedFieldTypeTarget)

    this._loadFieldProperties({ field_type: type, position: this.pendingPosition })
    this._setVisualizerDragging(false)
  }

  _isPropertiesPanelSuppressedType(type) {
    return type === "Divider"
  }

  _isInlineOnlyType(type) {
    return type === "Heading" || type === "InstructionBlock"
  }

  async _createInlineOnlyField(type, position) {
    const url = this._buildCreateUrl()
    const body = new FormData()
    body.append("asset_layout_field[field_type]", type)
    if (position) body.append("asset_layout_field[position]", String(position))
    body.append("autosave", "true")

    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken,
        },
        body,
      })

      const stream = await response.text()
      const createdFieldId = this._createdFieldIdFromStream(stream)
      Turbo.renderStreamMessage(stream)

      // Create responses may still refresh field_properties; clear it so the
      // panel stays closed for inline-only types.
      this._clearFieldPropertiesPanel()
      this.selectedType = null
      this.pendingPosition = null
      this.selectedFieldTypeTarget.classList.add("hidden")
      this._hideAllVisualizerForms()
      this._clearSelectedVisualizerForms()

      if (!response.ok || !createdFieldId) return

      this._selectAndFocusCreatedField(createdFieldId)

      window.dispatchEvent(new CustomEvent("form-builder:autosaved", {
        detail: { autosavedAt: new Date().toISOString() },
      }))
    } catch (error) {
      console.error("Failed to create field:", error)
    } finally {
      this._setVisualizerDragging(false)
    }
  }

  async _createPropertiesPanelSuppressedField(type, position) {
    const url = this._buildCreateUrl()
    const body = new FormData()
    body.append("asset_layout_field[field_type]", type)
    if (position) body.append("asset_layout_field[position]", String(position))
    body.append("autosave", "true")

    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken,
        },
        body,
      })

      const stream = await response.text()
      Turbo.renderStreamMessage(stream)

      // Keep the field properties side panel collapsed - the create response
      // refreshes the field_properties frame, so clear it after rendering.
      this._clearFieldPropertiesPanel()
      this.selectedType = null
      this.pendingPosition = null

      window.dispatchEvent(new CustomEvent("form-builder:autosaved", {
        detail: { autosavedAt: new Date().toISOString() },
      }))
    } catch (error) {
      console.error("Failed to create field:", error)
    } finally {
      this._setVisualizerDragging(false)
    }
  }

  _buildCreateUrl() {
    const url = new URL(this.fieldPropertiesUrlValue, window.location.origin)
    url.pathname = url.pathname.replace(/\/field_properties\/?$/, "")
    return url.toString()
  }

  _createdFieldIdFromStream(stream) {
    const template = document.createElement("template")
    template.innerHTML = stream

    const turboStream = template.content.querySelector(
      "turbo-stream[action='before'][target='new-field-preview']"
    )
    const fieldTemplate = turboStream?.querySelector("template")
    const field = fieldTemplate?.content?.querySelector(".form-builder-visualizer-field[data-id]")

    return field?.dataset?.id || null
  }

  _updateOrder() {
    const items = this.visualizerTarget.querySelectorAll(".form-builder-visualizer-field[data-id]")
    const positions = Array.from(items).map((item, index) => ({
      id: item.dataset.id,
      position: index + 1
    }))

    return fetch(this.visualizerTarget.dataset.updateUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: JSON.stringify({ positions })
    })
      .then(response => {
        if (!response.ok) throw new Error("Network response wasn't ok")

        flash("Positions updated", "success")
        window.dispatchEvent(new CustomEvent("form-builder:autosaved", {
          detail: { autosavedAt: new Date().toISOString() }
        }))
      })
      .catch(() => {
        flash("Something went wrong", "error")
      })
  }

  selectVisualizer(event) {
    if (event.target?.closest(".visualizer-button-group")) return

    // Visual-only elements (Divider) have no editable properties, so clicking
    // them must not open the side panel.
    if (this._isPropertiesPanelSuppressedType(event.currentTarget.dataset.type)) {
      return
    }

    // Heading/InstructionBlock are edited only in the workspace — select without
    // loading the field properties side panel.
    if (this._isInlineOnlyType(event.currentTarget.dataset.type)) {
      if (event.currentTarget.classList.contains("selected")) {
        this._clearFieldPropertiesPanel()
        return
      }
      this._selectVisualizerField(event.currentTarget, { loadProperties: false })
      return
    }

    if (event.currentTarget.classList.contains("selected") && this.fieldPropertiesFrameTarget.innerHTML.trim() !== "") {
      return
    }

    this._selectVisualizerField(event.currentTarget)
  }

  _selectVisualizerField(field, { loadProperties = true } = {}) {
    if (this._hasPendingAutosave()) {
      this._flushPendingAutosave()
      return
    }

    // Revert the previously-selected field's label before flushing so a queued
    // autosave sees the restored value (and skips, since the payload now
    // matches the last successful save).
    this._revertEmptyLabelInSelectedField()
    this._flushPendingAutosave()
    this.pendingLabelAutosaveSource = null

    const id = field.dataset.id

    this._clearFormBuilderErrorHighlights()
    this.selectedFieldTypeTarget.classList.add("hidden")
    this._hideAllVisualizerForms()
    this._clearSelectedVisualizerForms()

    field.classList.add("selected")
    this._deselectField(this.selectedFieldTypeTarget)

    this._activateVisualizerLabelFor(field)

    if (loadProperties) {
      this._loadFieldProperties({ field_id: id })
    } else {
      this._clearFieldPropertiesPanel()
      this._focusInlineEditor(field)
    }
  }

  _clearFieldPropertiesPanel() {
    // Selecting an inline-only field (Heading/InstructionBlock) while a
    // properties request is in flight empties the frame and drops src, which
    // can abort turbo:frame-load. Reset the loading flags here so later
    // keystrokes are not dropped by queueAutosave (only visualizer-label
    // edits are retried via _pendingLabelAutosave).
    this._formLoading = false
    this._pendingLabelAutosave = false
    this.fieldPropertiesFrameTarget.innerHTML = ""
    this.fieldPropertiesFrameTarget.removeAttribute("src")
  }

  /**
   * Turbo stream actions render on the next animation frame, so the created
   * field is not in the DOM when the create response resolves. Poll briefly
   * until it appears, then select and focus it.
   */
  _selectAndFocusCreatedField(fieldId, tries = 0) {
    const field = this.visualizerTarget.querySelector(
      `.form-builder-visualizer-field[data-id="${fieldId}"]`
    )

    if (!field) {
      if (tries < 30) {
        requestAnimationFrame(() => this._selectAndFocusCreatedField(fieldId, tries + 1))
      }
      return
    }

    field.classList.add("selected")
    this._activateVisualizerLabelFor(field)
    this._focusInlineEditor(field)
  }

  _focusInlineEditor(field, tries = 0) {
    if (!field || !field.isConnected) return

    const label = this._labelInputForContainer(field)
    if (label) {
      label.focus()
      // Auto-assigned labels ("Heading 1") should be replaced on first keystroke.
      if (typeof label.select === "function") label.select()
      return
    }

    const editorRoot = field.querySelector("[data-controller~='editor']")
    if (editorRoot) {
      editorRoot.dispatchEvent(new CustomEvent("editor:focus"))
      return
    }

    const editable = field.querySelector("[contenteditable='true']")
    if (editable) {
      editable.focus()
      return
    }

    // The heading input / TipTap mount may still be rendering.
    if (tries < 30) {
      requestAnimationFrame(() => this._focusInlineEditor(field, tries + 1))
    }
  }

  closeSidebar() {
    this._revertEmptyLabelInSelectedField()
    this._flushPendingAutosave()

    this._clearFormBuilderErrorHighlights()
    this._hideAllVisualizerForms()
    this._clearSelectedVisualizerForms()
    this._deactivateAllVisualizerLabels()

    this.selectedFieldTypeTarget.classList.add("hidden")
    this.selectedType = null
    this.pendingPosition = null
    this._formLoading = false
    this._pendingLabelAutosave = false

    this._clearFieldPropertiesPanel()
  }

  toggleRadarLocationCheckbox({ target }) {
    if (!this.hasRadarLocationGroupTarget) return

    const show = target.value && target.value === this.radarLocationGroupTarget.dataset.locationLayoutId
    this.radarLocationGroupTarget.classList.toggle("hidden", !show)
  }

  toggleIntegratorSlugGroup(event) {
    const form = this._currentForm(event)
    if (!form) return

    const selectEl = event?.target?.tagName === "SELECT"
      ? event.target
      : this._firstTargetInForm(this.radarDataPointSelectorTargets, form)

    if (!selectEl) return

    const slugGroup = this._firstTargetInForm(this.integratorSlugGroupTargets, form)

    if (slugGroup) {
      if (selectEl.value) {
        slugGroup.classList.add("disabled")
      } else {
        slugGroup.classList.remove("disabled")
      }
    }
  }

  fetchIntegratorDataPoints(event) {
    const form = this._currentForm(event)
    if (!form) return

    const selectEl = event?.target?.tagName === "SELECT"
      ? event.target
      : this._firstTargetInForm(this.integratorSlugSelectorTargets, form)

    if (!selectEl) return

    const selectedValue = selectEl.value
    const selectedOption = selectEl.querySelector(`option[value="${selectedValue}"]`) || selectEl.selectedOptions[0]
    const integratorId = selectedOption?.dataset?.id
    const slugGroup = this._firstTargetInForm(this.integratorSlugGroupTargets, form)
    const fieldType = slugGroup ? slugGroup.dataset.type : null

    const radarDataPointGroup = this._firstTargetInForm(this.radarDataPointGroupTargets, form)
    const dpGroup = this._firstTargetInForm(this.integratorDataPointGroupTargets, form)
    const dpSelector = this._firstTargetInForm(this.integratorDataPointSelectorTargets, form)

    if (!integratorId) {
      if (dpGroup) {
        dpGroup.classList.add("hidden")
        dpGroup.classList.remove("required")
      }
      if (dpSelector) dpSelector.innerHTML = '<option value="">Select an option</option>'

      // re-enable radar data point if integrator is cleared
      if (radarDataPointGroup) {
        radarDataPointGroup.classList.remove("disabled")
      }
      return
    }

    // disable radar data point if integrator is selected
    if (radarDataPointGroup) {
      radarDataPointGroup.classList.add("disabled")
    }

    const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content")

    fetch(`/admin/integrators/${integratorId}/data_fill_fields`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ type: fieldType })
    })
      .then(response => {
        if (!response.ok) throw new Error("Network response wasn't ok")
        return response.json()
      })
      .then(data => {
        this.updateDataPointDropdownForForm(data, form)
      })
      .catch(err => {
        console.error("Error fetching data points:", err)
      })
  }

  updateDataPointDropdownForForm(data, form) {
    if (!form) return

    const dpSelector = this._firstTargetInForm(this.integratorDataPointSelectorTargets, form)
    const dpGroup = this._firstTargetInForm(this.integratorDataPointGroupTargets, form)

    if (!dpSelector || !dpGroup) return

    dpSelector.innerHTML = '<option value="">Select an option</option>'
    const existing = dpSelector.dataset.selectedValue

    const dataPoints = data.fields || []
    dataPoints.forEach(dp => {
      const option = document.createElement("option")
      option.value = dp
      option.textContent = dp
      if (existing && dp === existing) option.selected = true
      dpSelector.appendChild(option)
    })

    dpGroup.classList.remove("hidden")
    dpGroup.classList.add("required")
  }

  // === Private helpers ===

  queueAutosave(event) {
    if (this._isLabelInput(event.target)) {
      this.pendingLabelAutosaveSource = event.target
    }

    // Visualizer label inputs sit outside the form and link via the HTML
    // active field's properties form; gate them on the loading flag so a keystroke that
    // arrives before the freshly-requested form lands does not autosave to a
    // stale form (i.e. a different field's properties).
    if (this._formLoading) {
      if (this._isVisualizerLabel(event.target)) this._pendingLabelAutosave = true
      return
    }

    const form = this._formForEventTarget(event.target)
    if (!form) return

    if (!this._hasValidIntegratorPair(form)) return

    const delay = this._autosaveDelay(event)
    this._scheduleAutosave(form, delay)
  }

  handleNavigationKey(event) {
    if (!this._isNavigationKey(event)) return
    if (!this.autosaveTimeout) return

    const form = this._formForEventTarget(event.target)
    if (!form) return
    if (!this._hasValidIntegratorPair(form)) return

    this._scheduleAutosave(form, this.autosaveDebounceMsValue)
  }

  _loadFieldProperties(params) {
    const url = new URL(this.fieldPropertiesUrlValue, window.location.origin)
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return
      url.searchParams.set(key, value)
    })
    this._formLoading = true
    this.fieldPropertiesFrameTarget.src = url.toString()
  }

  _syncPendingPositionIntoForm(form) {
    if (this.pendingPosition === null || this.pendingPosition === undefined) return

    if (this._isNewFieldForm(form)) {
      let positionInput = form.querySelector("input[name='asset_layout_field[position]']")
      if (!positionInput) {
        positionInput = document.createElement("input")
        positionInput.type = "hidden"
        positionInput.name = "asset_layout_field[position]"
        form.appendChild(positionInput)
      }
      positionInput.value = String(this.pendingPosition)
    } else {
      this.pendingPosition = null
    }
  }

  _isNewFieldForm(form) {
    if (!form) return false

    const methodInput = form.querySelector("input[name='_method']")
    if (methodInput && /^(patch|put|delete)$/i.test(methodInput.value)) return false

    return true
  }

  _currentForm(event) {
    if (event?.target instanceof Element) {
      return this._formForEventTarget(event.target)
    }

    return this.fieldPropertiesFrameTarget.querySelector("form.form-builder-form")
  }

  /**
   * Resolves the form-builder form for an event target, supporting inputs
   * that live outside the form and link to it via the HTML `form` attribute.
   *
   * Heading/InstructionBlock labels (and their TipTap editors) submit via an inline
   * form in the visualizer. Other field types still mirror the visualizer
   * label into the side-panel form.
   */
  _formForEventTarget(target) {
    if (!(target instanceof Element)) return null

    if (this._isVisualizerLabel(target)) {
      const inlineForm = target.closest("form.form-builder-form")
      if (inlineForm) return inlineForm

      return this.fieldPropertiesFrameTarget.querySelector("form.form-builder-form")
    }

    const linkedForm = target instanceof HTMLElement ? target.form : null
    if (linkedForm && linkedForm.classList.contains("form-builder-form")) {
      return linkedForm
    }

    return target.closest("form.form-builder-form")
  }

  _isVisualizerLabel(target) {
    return target instanceof Element && target.classList.contains("visualizer-label-input")
  }

  _isFieldPropertiesLabel(target) {
    return this.hasFieldPropertiesLabelTarget && this.fieldPropertiesLabelTargets.includes(target)
  }

  _isLabelInput(target) {
    return this._isVisualizerLabel(target) || this._isFieldPropertiesLabel(target)
  }

  /** Reads which visualizer label input is currently flagged as the active one. */
  _activeVisualizerLabel() {
    if (!this.hasVisualizerLabelTarget) return null
    return this.visualizerLabelTargets.find((el) => el.dataset.active === "true") || null
  }

  /**
   * Promotes the label input inside `container` (a visualizer field row or the
   * new-field preview) as the active inline label editor.
   *
   * Any previously-active label that has been emptied is reverted to its
   * server value here — switching fields is the canonical "I'm done with that
   * label" signal.
   */
  _activateVisualizerLabelFor(container) {
    if (!this.hasVisualizerLabelTarget) return

    const activeInput = this._labelInputForContainer(container)
    this.visualizerLabelTargets.forEach((el) => {
      if (el === activeInput) {
        el.dataset.active = "true"
      } else {
        if (el.dataset.active === "true") this._revertIfEmptied(el)
        delete el.dataset.active
      }
    })
  }

  _deactivateAllVisualizerLabels() {
    if (!this.hasVisualizerLabelTarget) return
    this.visualizerLabelTargets.forEach((el) => {
      if (el.dataset.active === "true") this._revertIfEmptied(el)
      delete el.dataset.active
    })
  }

  _labelInputForContainer(container) {
    if (!container) return null

    if (container === this.selectedFieldTypeTarget) {
      const visiblePreview = this.selectedFieldTypeTarget.querySelector(
        "[data-field-type]:not(.hidden)"
      )
      const root = visiblePreview || this.selectedFieldTypeTarget
      return root.querySelector(".visualizer-label-input")
    }

    return container.querySelector(".visualizer-label-input")
  }

  /** Clears server-rendered validation styling when switching fields or closing the sidebar. */
  _clearFormBuilderErrorHighlights() {
    if (this.hasVisualizerTarget) {
      this.visualizerTarget.querySelectorAll(".form-builder-visualizer-field").forEach((el) => {
        el.classList.remove("error")
      })
    }
  }

  /** After a failed "create field" submit, restore the new-field visualizer + sidebar (no persisted id yet). */
  _restorePendingNewFieldAfterError(type) {
    this.selectedType = type

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.replaceWith(this.visualizerTarget)
      this.visualizerTarget.classList.remove("hidden")
    }

    this.selectedFieldTypeTarget.classList.remove("hidden")
    this._hideAllVisualizerForms()
    this._clearSelectedVisualizerForms()
    this._showFormForType(type)
    this._activateVisualizerLabelFor(this.selectedFieldTypeTarget)

    this._deselectField(this.selectedFieldTypeTarget)
    this.selectedFieldTypeTarget.classList.add("error")
  }

  _hideAllVisualizerForms() {
    this.selectedFieldTypeTarget.querySelectorAll("[data-field-type]").forEach(el => {
      el.classList.add("hidden")
    })
  }

  _clearSelectedVisualizerForms() {
    this.visualizerTarget.querySelectorAll(".form-builder-visualizer-field").forEach(el => {
      this._deselectField(el)
    })
  }

  _showFormForType(type) {
    this.selectedFieldTypeTarget.classList.add("selected")
    const selectedForm = this.selectedFieldTypeTarget.querySelector(`[data-field-type="${type}"]`)
    if (selectedForm) {
      selectedForm.classList.remove("hidden")
      selectedForm.scrollIntoView({
        behavior: "smooth",
        block: "center"
      })
    }
  }

  _clearNewFieldPreviewLabel() {
    this.selectedFieldTypeTarget.querySelectorAll(".visualizer-label-input").forEach((previewLabel) => {
      previewLabel.value = ""
      previewLabel.defaultValue = ""
    })

    if (this.selectedFieldTypeTarget.contains(this.pendingLabelAutosaveSource)) {
      this.pendingLabelAutosaveSource = null
    }
  }

  _autosaveDelay(event) {
    const target = event.target

    // Typing should debounce until the user pauses.
    if (event.type === "input") return this.autosaveDebounceMsValue

    if (target.tagName === "SELECT") return 0
    if (target.type === "checkbox" || target.type === "radio") return 0

    // `change` on text-like inputs fires on blur (e.g. tabbing to another
    // field); keep the debounce instead of saving immediately.
    return this.autosaveDebounceMsValue
  }

  _scheduleAutosave(form, delay) {
    clearTimeout(this.autosaveTimeout)
    this.pendingAutosaveForm = form
    this.autosaveTimeout = setTimeout(() => {
      this.autosaveTimeout = null
      this.pendingAutosaveForm = null

      // Slash (`/` / `+`) and other TipTap suggestion menus insert transient
      // query text. Saving mid-menu remounts/interrupts the editor — keep
      // deferring until the decoration is gone, then save once.
      if (this._isTiptapSuggestionActive(form)) {
        this._scheduleAutosave(form, this.autosaveDebounceMsValue)
        return
      }

      this._performAutosave(form)
    }, delay)
  }

  /**
   * True while a TipTap suggestion menu (slash, emoji, mention) is active in
   * `root` — detected via the suggestion plugin's decoration class.
   */
  _isTiptapSuggestionActive(root) {
    if (!(root instanceof Element)) return false

    return Boolean(root.querySelector(
      ".tiptap-slash-decoration, .tiptap-emoji-decoration, .tiptap-mention-decoration",
    ))
  }

  _hasValidIntegratorPair(form) {
    const slugSelect = this._firstTargetInForm(this.integratorSlugSelectorTargets, form)
    const dpSelect = this._firstTargetInForm(this.integratorDataPointSelectorTargets, form)

    if (!slugSelect || !dpSelect) return true

    const hasSlug = slugSelect.value !== ""
    const hasDataPoint = dpSelect.value !== ""

    return (hasSlug && hasDataPoint) || (!hasSlug && !hasDataPoint)
  }

  _serializeForm(form) {
    return new URLSearchParams(new FormData(form)).toString()
  }

  _isNavigationKey(event) {
    return [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "PageUp",
      "PageDown",
    ].includes(event.key)
  }

  _firstTargetInForm(targets, form) {
    return targets.find((target) => target.closest("form.form-builder-form") === form) || null
  }

  _clearQueuedAutosave() {
    clearTimeout(this.autosaveTimeout)
    this.autosaveTimeout = null
    this.pendingAutosaveForm = null

    this.autosaveAbortControllersByForm.forEach((controller) => controller.abort())
    this.autosaveAbortControllersByForm.clear()
    this.pendingAutosaveByForm.clear()
  }

  _hasPendingAutosave() {
    return Boolean(this.autosaveTimeout) ||
      this.autosaveAbortControllersByForm.size > 0 ||
      this.pendingAutosaveByForm.size > 0
  }

  _flushPendingAutosave() {
    if (!this.autosaveTimeout) return

    const form = this.pendingAutosaveForm || this._currentForm()
    clearTimeout(this.autosaveTimeout)
    this.autosaveTimeout = null
    this.pendingAutosaveForm = null

    if (!form || !form.isConnected) return
    if (!this._hasValidIntegratorPair(form)) return

    this._performAutosave(form)
  }

  /**
   * If the user emptied a visualizer label before switching fields, restore the
   * last server-rendered value (`defaultValue`). The server rejects blank
   * labels, so reverting prevents silent data loss when an accidental clear is
   * followed by a focus change.
   */
  _revertIfEmptied(input) {
    if (!input) return
    if (input.value !== "") return
    if (!input.defaultValue) return

    input.value = input.defaultValue
  }

  /**
   * Single source of truth for "this visualizer field is no longer selected":
   * revert its label if it was emptied, then drop the `.selected` class.
   *
   * Routing every `.selected` removal through this helper guarantees the
   * revert fires the moment a field stops being selected — regardless of which
   * code path triggered the deselection.
   */
  _deselectField(el) {
    if (!el) return
    if (el.classList.contains("selected")) {
      const labelInput = el.querySelector(".visualizer-label-input")
      this._revertIfEmptied(labelInput)
    }
    el.classList.remove("selected")
  }

  /**
   * Pre-flush guard: revert the currently-selected field's label if it was
   * emptied, BEFORE a queued autosave fires. Looks up the selected field by
   * `.selected` class (rather than the `data-active` flag on the label) so
   * the revert is independent of activation timing.
   */
  _revertEmptyLabelInSelectedField() {
    const selected = this.element.querySelector(
      ".form-builder-visualizer-field.selected"
    )
    if (!selected) return
    this._revertIfEmptied(selected.querySelector(".visualizer-label-input"))
  }

  async _performAutosave(form) {
    if (!form.isConnected) return

    const formKey = this._formKey(form)
    this._prepareLabelForAutosave(form)
    const savedLabelValue = this._labelValueForAutosaveSource(form)
    const payload = this._serializeForm(form)
    if (payload === this.lastAutosavePayloadByForm.get(formKey)) return
    const focusSnapshot = this._captureFocusSnapshot(form)

    const existingController = this.autosaveAbortControllersByForm.get(formKey)
    if (existingController) {
      this.pendingAutosaveByForm.set(formKey, true)
      return
    }
    const abortController = new AbortController()
    this.autosaveAbortControllersByForm.set(formKey, abortController)

    const body = new FormData(form)
    body.append("autosave", "true")

    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    try {
      const response = await fetch(form.action, {
        method: (form.method || "post").toUpperCase(),
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken
        },
        body,
        signal: abortController.signal
      })

      const stream = await response.text()
      // Keep the workspace DOM stable while the user is typing inline
      // (Heading label / InstructionBlock TipTap). Replacing the visualizer field
      // would remount the editor and drop caret position.
      const skipVisualizerReplacement =
        response.ok && (
          focusSnapshot?.isVisualizerLabel ||
          Boolean(form.closest(".form-builder-visualizer-field"))
        )

      Turbo.renderStreamMessage(
        skipVisualizerReplacement
          ? this._withoutVisualizerFieldReplacements(stream)
          : stream
      )

      if (!response.ok) {
        this._revertRejectedLabelAutosave(form)
        this._restoreFocusSnapshot(focusSnapshot)
        return
      }

      if (savedLabelValue !== null) this._syncMirroredLabelInputs(savedLabelValue)
      window.dispatchEvent(new CustomEvent("form-builder:autosaved", {
        detail: { autosavedAt: new Date().toISOString() }
      }))
      this._restoreFocusSnapshot(focusSnapshot)
      this.lastAutosavePayloadByForm.set(formKey, payload)
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Autosave failed:", error)
      }
    } finally {
      if (this.autosaveAbortControllersByForm.get(formKey) === abortController) {
        this.autosaveAbortControllersByForm.delete(formKey)
      }

      if (this.pendingAutosaveByForm.get(formKey)) {
        this.pendingAutosaveByForm.delete(formKey)
        // Retry the same form instance that was marked pending. `_currentForm()`
        // only looks in the side-panel frame, so inline Heading/InstructionBlock
        // forms would never be retried after a slow autosave.
        if (
          form.isConnected &&
          this._formKey(form) === formKey &&
          this._hasValidIntegratorPair(form)
        ) {
          this._performAutosave(form)
        }
      }
    }
  }

  _formKey(form) {
    return `${(form.method || "post").toUpperCase()}:${form.action}`
  }

  _prepareLabelForAutosave(form) {
    const source = this._currentLabelAutosaveSource(form)
    if (!this._isVisualizerLabel(source)) return

    const fieldPropertiesLabel = this._fieldPropertiesLabelForForm(form)
    if (fieldPropertiesLabel) fieldPropertiesLabel.value = source.value
  }

  _labelValueForAutosaveSource(form) {
    const source = this._currentLabelAutosaveSource(form)
    if (this._isLabelInput(source)) return source.value

    const fieldPropertiesLabel = this._fieldPropertiesLabelForForm(form)
    return fieldPropertiesLabel ? fieldPropertiesLabel.value : null
  }

  _currentLabelAutosaveSource(form) {
    const source = this.pendingLabelAutosaveSource
    if (!this._isLabelInput(source)) return null

    const fieldPropertiesLabel = this._fieldPropertiesLabelForForm(form)
    if (source === fieldPropertiesLabel) return source

    const activeVisualizerLabel = this._activeVisualizerLabel()
    if (source === activeVisualizerLabel) return source

    return null
  }

  // Check if the pending visualizer label differs from the form's field properties label. If it does, return true. 
  // This is used to make sure autosaving still works when the user is typing in the visualizer label before the properties frame loads.
  _pendingVisualizerLabelDiffersFromForm(form) {
    const source = this.pendingLabelAutosaveSource
    if (!this._isVisualizerLabel(source)) return false

    const fieldPropertiesLabel = this._fieldPropertiesLabelForForm(form)
    return fieldPropertiesLabel && source.value !== fieldPropertiesLabel.value
  }

  _syncMirroredLabelInputs(value) {
    const activeVisualizerLabel = this._activeVisualizerLabel()
    const fieldPropertiesLabel = this.hasFieldPropertiesLabelTarget
      ? this.fieldPropertiesLabelTargets[0]
      : null

    const inputs = [activeVisualizerLabel, fieldPropertiesLabel]
    inputs.forEach((input) => {
      if (!input) return
      if (
        input === activeVisualizerLabel &&
        this.selectedFieldTypeTarget.contains(input) &&
        !this.selectedFieldTypeTarget.contains(this.pendingLabelAutosaveSource)
      ) {
        return
      }

      input.value = value
      input.defaultValue = value
    })
  }

  _revertRejectedLabelAutosave(form) {
    const source = this.pendingLabelAutosaveSource
    if (!this._isLabelInput(source)) return

    const activeVisualizerLabel = this._activeVisualizerLabel()
    const fieldPropertiesLabel = this._fieldPropertiesLabelForForm(form)
    const lastSavedValue = [source, activeVisualizerLabel, fieldPropertiesLabel]
      .find((input) => input && typeof input.defaultValue === "string")
      ?.defaultValue

    if (lastSavedValue === undefined) return

    const labelInputs = [source, activeVisualizerLabel, fieldPropertiesLabel]
    labelInputs.forEach((input) => {
      if (!input) return
      input.value = lastSavedValue
    })
  }

  _syncFieldPropertiesLabelFromActiveVisualizer(form) {
    const activeVisualizerLabel = this._activeVisualizerLabel()
    const fieldPropertiesLabel = this._fieldPropertiesLabelForForm(form)
    if (!activeVisualizerLabel || !fieldPropertiesLabel) return

    fieldPropertiesLabel.value = activeVisualizerLabel.value
    fieldPropertiesLabel.defaultValue = activeVisualizerLabel.defaultValue
  }

  _fieldPropertiesLabelForForm(form) {
    if (!form) return null
    if (!this.hasFieldPropertiesLabelTarget) return form.querySelector("[name='asset_layout_field[label]']")

    return this.fieldPropertiesLabelTargets.find((target) => target.closest("form.form-builder-form") === form) ||
      form.querySelector("[name='asset_layout_field[label]']")
  }

  _withoutVisualizerFieldReplacements(stream) {
    const template = document.createElement("template")
    template.innerHTML = stream

    template.content.querySelectorAll("turbo-stream[action='replace'][target^='asset_layout_field_']").forEach((el) => {
      el.remove()
    })

    return template.innerHTML
  }

  _captureFocusSnapshot(form) {
    const active = document.activeElement
    if (!(active instanceof HTMLElement)) return null

    // Visualizer label inputs sit outside the form (linked via `form` attr) but
    // are still effectively part of this autosave; capture them too so focus
    // is restored after the visualizer field is re-rendered by Turbo.
    const isVisualizerLabel = this._isVisualizerLabel(active)
    if (!form.contains(active) && !isVisualizerLabel) return null

    const snapshot = {
      name: active.getAttribute("name"),
      id: active.getAttribute("id"),
      isVisualizerLabel,
      selectionStart: null,
      selectionEnd: null,
    }

    if (typeof active.selectionStart === "number" && typeof active.selectionEnd === "number") {
      snapshot.selectionStart = active.selectionStart
      snapshot.selectionEnd = active.selectionEnd
    }

    return snapshot
  }

  _restoreFocusSnapshot(snapshot) {
    if (!snapshot) return

    let next = null

    if (snapshot.isVisualizerLabel) {
      // After Turbo replaces the visualizer field, find the new active label
      // input (re-activated by `handleFrameLoad`) and refocus it.
      next = this._activeVisualizerLabel()
    } else {
      const frame = this.fieldPropertiesFrameTarget

      if (snapshot.name) {
        next = Array.from(frame.querySelectorAll("[name]")).find(
          (el) => el.getAttribute("name") === snapshot.name,
        ) || null
      }

      if (!next && snapshot.id) {
        next = frame.querySelector(`#${this._escapeSelector(snapshot.id)}`)
      }
    }

    if (!(next instanceof HTMLElement)) return

    // If the input survived the Turbo render it never lost focus and the
    // caret is wherever the user last typed; re-applying the pre-save
    // snapshot would yank the caret backwards mid-word. Only restore the
    // caret when focus was actually lost.
    const wasFocused = next === document.activeElement
    next.focus()
    if (!wasFocused && typeof next.setSelectionRange === "function" && snapshot.selectionStart !== null && snapshot.selectionEnd !== null) {
      next.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd)
    }
  }

  _escapeSelector(value) {
    if (typeof window !== "undefined" && window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value)
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&")
  }

}
